import useApiService from 'services/api'
import { ProductPrediction } from 'models/deals'
import { ProgressChart } from './index'
import { getCompanyPageChartColor } from 'utils/companyPageChartColors'
import { formatCurrency, formatVolume } from 'utils/formatting'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { useMemo } from 'react'

interface ProductMixItem {
  name: string
  value: number
  color: string
  percentage: number
}

interface ForecastingSectionProps {
  chainId?: number
  doorId?: number
}

// Fixed color used for the aggregated "Other Products" slice in both charts so
// it never collides with a real product's color from the palette.
const OTHER_PRODUCTS_COLOR = '#9CA3AF' // gray-400

// Build a stable product-name → color map up front so the revenue and volume
// charts (and the shared legend) all agree on a product's color, regardless
// of how each chart sorts its rows.
function buildProductColorMap(
  predictions: ProductPrediction[]
): Record<string, string> {
  const namesByRevenue = predictions
    .map((p) => ({
      name: p.product.name || 'Unknown Product',
      revenue: p.taro || 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)

  const colorMap: Record<string, string> = {}
  let nextIndex = 0
  namesByRevenue.forEach(({ name }) => {
    if (!(name in colorMap)) {
      colorMap[name] = getCompanyPageChartColor(nextIndex)
      nextIndex += 1
    }
  })
  return colorMap
}

function processProductsForDisplay(
  predictions: ProductPrediction[],
  type: 'revenue' | 'volume',
  colorMap: Record<string, string>
): ProductMixItem[] {
  // Get annual values and sort by value (descending)
  const products = predictions
    .map((p) => ({
      name: p.product.name || 'Unknown Product',
      value:
        type === 'revenue'
          ? p.taro || 0
          : // For non-recurring products, backend returns total volume (don't multiply)
            // For recurring products, backend returns monthly volume (multiply by 12 for annual)
            p.product.is_not_recurring
            ? p.predicted_volume
            : p.predicted_volume * 12,
    }))
    .sort((a, b) => b.value - a.value)

  const total = products.reduce((sum, p) => sum + p.value, 0)
  const result: ProductMixItem[] = []
  let otherValue = 0

  products.forEach((product) => {
    const percentage = total > 0 ? (product.value / total) * 100 : 0

    // Show individually if: 0 value, ≥5%, or no total
    if (product.value === 0 || percentage >= 5 || total === 0) {
      result.push({
        name: product.name,
        value: product.value,
        color:
          colorMap[product.name] ?? getCompanyPageChartColor(result.length),
        percentage: Math.round(percentage),
      })
    } else {
      otherValue += product.value
    }
  })

  // Add "Other Products" if any
  if (otherValue > 0) {
    result.push({
      name: 'Other Products',
      value: otherValue,
      color: OTHER_PRODUCTS_COLOR,
      percentage: Math.round((otherValue / total) * 100),
    })
  }

  return result
}

export function ForecastingSection({
  chainId,
  doorId,
}: ForecastingSectionProps) {
  const api = useApiService()
  const { preferences, isLoading: isPreferencesLoading } = usePreferences()
  const { data: predictions, isLoading } = api.useGetProductPredictions({
    chainProxyId: chainId,
    doorProxyId: doorId,
    enabled: !!(chainId || doorId), // Only fetch if we have an ID
  })

  const reportingUnit = useMemo(
    () => preferences?.reporting_unit || 'lbs',
    [preferences?.reporting_unit]
  )

  // Show loading state
  if (isLoading || isPreferencesLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-center h-24">
        <div className="text-xs text-gray-500">Loading forecasting data...</div>
      </div>
    )
  }

  // Show empty state if no data
  if (!predictions || predictions.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-center h-24">
        <div className="text-xs text-gray-500">
          No forecasting data available
        </div>
      </div>
    )
  }

  // Calculate totals and process products
  const totalRevenue = predictions.reduce((sum, p) => sum + (p.taro || 0), 0)
  const totalVolume = predictions.reduce(
    (sum, p) =>
      sum +
      // For non-recurring: backend returns total volume
      // For recurring: backend returns monthly, multiply by 12 for annual
      (p.product.is_not_recurring
        ? p.predicted_volume
        : p.predicted_volume * 12),
    0
  )

  const colorMap = buildProductColorMap(predictions)
  const revenueProducts = processProductsForDisplay(
    predictions,
    'revenue',
    colorMap
  )
  const volumeProducts = processProductsForDisplay(
    predictions,
    'volume',
    colorMap
  )

  return (
    <div className="bg-gray-50 rounded-lg p-3 flex flex-col gap-6">
      {/* Annual Revenue Opportunity - shows revenue-based product mix */}
      <ProgressChart
        label="ANNUAL REVENUE OPP."
        value={formatCurrency(totalRevenue)}
        items={revenueProducts}
        tooltipValueFormatter={(item) => formatCurrency(item.value)}
      />

      {/* Annual Volume Opportunity - shows volume-based product mix */}
      <ProgressChart
        label="ANNUAL VOLUME OPP."
        value={formatVolume(totalVolume, reportingUnit)}
        items={volumeProducts}
        tooltipValueFormatter={(item) =>
          formatVolume(item.value, reportingUnit)
        }
        legend={{
          title: 'Products',
          showLink: true,
          linkHref: '/products',
          columns: 2,
          showPercentages: false,
        }}
      />
    </div>
  )
}
